import React, { useRef, useEffect } from 'react'

import { supportedLanguages } from '@common'
import cx from 'classnames'

import { IconButton } from '@/components/buttons/IconButton'
import { CartButtonIcon } from '@/components/Icons'
import { useLocale } from '@/features/locale/useLocale'
import { clearNotification } from '@/features/notifications/notificationSlice'
import { useAppDispatch } from '@/utils/useAppDispatch'
import { useAppSelector } from '@/utils/useAppSelector'

import { useCart } from './cart-provider'

import styles from './CartButton.module.scss'

export const CartButton: React.FC<{ className?: string }> = ({ className }) => {
  const { isOpen, setIsOpen } = useCart()
  const gameId = useAppSelector((state) => state.common.currentGame)
  const locale = useLocale()
  const addItemNotification = useAppSelector((state) =>
    state.notifications.notifications.find((a) => a.type === 'ADD_PRODUCT'),
  )

  const dispatch = useAppDispatch()
  const rows = useAppSelector((state) => state.checkout.rows).filter((r) => r.product.game === gameId)
  const addedRowQuantity = rows.find((row) => addItemNotification?.productId === row.product.id)?.quantity ?? 0
  const contentRef = useRef<HTMLDivElement>(null)
  const parentRef = useRef<HTMLButtonElement>(null)

  const close = (event?: MouseEvent | TouchEvent | KeyboardEvent) => {
    if (event?.type === 'mousedown' && (event.target as HTMLElement)?.closest?.('[data-type="buy"]')) {
      return
    }
    if (contentRef.current?.contains(event?.target as Node)) {
      return
    }
    dispatch(clearNotification('ADD_PRODUCT'))
    if (!isOpen) {
      return
    }
    event?.preventDefault()
    setIsOpen(false)
  }

  const toggle = (e?: MouseEvent) => {
    if (isOpen) {
      close(e)
    } else {
      setIsOpen(true)
      dispatch(clearNotification('ADD_PRODUCT'))
    }
  }

  const itemCount = rows.reduce<number>((acc, curr) => acc + curr.quantity, 0)
  const formattedItemCount = new Intl.NumberFormat(supportedLanguages[locale].locale).format(Math.min(itemCount, 99))

  useEffect(() => {
    if (addItemNotification?.skipOpen) {
      return
    }

    if (addItemNotification?.type === 'ADD_PRODUCT') {
      setIsOpen(true)
    }
  }, [addItemNotification, setIsOpen])

  const showItemAddedAnimation = !isOpen && addedRowQuantity > 1 && addedRowQuantity < 10

  return (
    <div className={cx(className, styles.CartButton, { [styles.Open]: isOpen })} data-cy="cart-button-container">
      <IconButton
        onClick={toggle}
        data-cy="cart-button"
        ref={parentRef}
        className={cx(styles.CartIcon, {
          [styles.Empty]: itemCount === 0,
          [styles.ItemAdded]: showItemAddedAnimation,
        })}
        data-after-content={formattedItemCount}
      >
        <CartButtonIcon />
      </IconButton>
    </div>
  )
}
